/**
 * Every string the authenticated workspace renders, in both languages.
 *
 * Same discipline as the landing page's `translations.ts`: one dictionary,
 * typed so a missing Arabic string is a compile error, Arabic authored for
 * the reader rather than translated word for word. The keys the handoff
 * specifies verbatim (`nav.signin`, `auth.title`, `priority.suggested`,
 * `github.unverified`, `denied.title` and so on) carry that exact copy.
 *
 * Voice rules, carried from the design system: sentence case, no emoji, no
 * exclamation marks, never a bare "Done" or "Failed" without naming the
 * dimension, and never implying a remote operation succeeded before the
 * server said so.
 *
 * **The Arabic copy has not been reviewed by a native speaker.** It is listed
 * in the pull request for human review, exactly as the landing page's was.
 */

import type { Language } from '../i18n/translations.js';

import { SHELL_COPY_AR, SHELL_COPY_EN, type ShellCopy } from './copy/shell.js';
import { SIGN_IN_COPY_AR, SIGN_IN_COPY_EN, type SignInCopy } from './copy/sign-in.js';
import {
  DASHBOARD_COPY_AR,
  DASHBOARD_COPY_EN,
  type DashboardCopy,
} from './copy/dashboard.js';
import {
  TASK_BOARD_COPY_AR,
  TASK_BOARD_COPY_EN,
  type TaskBoardCopy,
} from './copy/task-board.js';
import {
  TASK_DETAIL_COPY_AR,
  TASK_DETAIL_COPY_EN,
  type TaskDetailCopy,
} from './copy/task-detail.js';
import {
  TASK_PROPOSAL_COPY_AR,
  TASK_PROPOSAL_COPY_EN,
  type TaskProposalCopy,
} from './copy/task-proposal.js';
import {
  DATA_ROOM_COPY_AR,
  DATA_ROOM_COPY_EN,
  type DataRoomCopy,
} from './copy/data-room.js';
import {
  CONTRIBUTIONS_COPY_AR,
  CONTRIBUTIONS_COPY_EN,
  type ContributionsCopy,
} from './copy/contributions.js';
import { TEAM_COPY_AR, TEAM_COPY_EN, type TeamCopy } from './copy/team.js';
import {
  STATUS_FEEDBACK_COPY_AR,
  STATUS_FEEDBACK_COPY_EN,
  type StatusFeedbackCopy,
} from './copy/status-feedback.js';

/**
 * The whole dictionary, assembled from the per-feature files under `copy/`
 * in the order the sections have always had.
 */
export interface WorkspaceCopy
  extends
    ShellCopy,
    SignInCopy,
    DashboardCopy,
    TaskBoardCopy,
    TaskDetailCopy,
    TaskProposalCopy,
    DataRoomCopy,
    ContributionsCopy,
    TeamCopy,
    StatusFeedbackCopy {}

const EN: WorkspaceCopy = {
  ...SHELL_COPY_EN,
  ...SIGN_IN_COPY_EN,
  ...DASHBOARD_COPY_EN,
  ...TASK_BOARD_COPY_EN,
  ...TASK_DETAIL_COPY_EN,
  ...TASK_PROPOSAL_COPY_EN,
  ...DATA_ROOM_COPY_EN,
  ...CONTRIBUTIONS_COPY_EN,
  ...TEAM_COPY_EN,
  ...STATUS_FEEDBACK_COPY_EN,
};

const AR: WorkspaceCopy = {
  ...SHELL_COPY_AR,
  ...SIGN_IN_COPY_AR,
  ...DASHBOARD_COPY_AR,
  ...TASK_BOARD_COPY_AR,
  ...TASK_DETAIL_COPY_AR,
  ...TASK_PROPOSAL_COPY_AR,
  ...DATA_ROOM_COPY_AR,
  ...CONTRIBUTIONS_COPY_AR,
  ...TEAM_COPY_AR,
  ...STATUS_FEEDBACK_COPY_AR,
};

export const WORKSPACE_COPY: Readonly<Record<Language, WorkspaceCopy>> = {
  en: EN,
  ar: AR,
};
